import {
  PaymentsAPIClient,
  PaymentsApiErrorCodes,
  PaymentsApiErrorResponse,
  PaymentsApiException,
} from '@kotipizzagroup/kotipizza-payments-api-client';
import { axiosWithJsonParse } from './utils/axios';
import { config } from '../utils/config';

let client: PaymentsAPIClient | null = null;

const getPaymentsApiClient = (): PaymentsAPIClient => {
  if (client) return client;
  client = new PaymentsAPIClient(config.paymentsApiEndpoint, axiosWithJsonParse);
  return client;
};

export const paymentsApiClient = getPaymentsApiClient();

export const getPaymentsApiErrorMessage = (code?: PaymentsApiErrorCodes | null): string => {
  if (code) {
    return paymentsApiErrorMessages[code];
  }
  return paymentsApiErrorMessages[PaymentsApiErrorCodes.GenericUnknownError];
};

export function isPaymentsApiErrorResponse(obj: unknown): obj is PaymentsApiErrorResponse {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const maybe = obj as Partial<PaymentsApiErrorResponse>;

  const isValidErrorCode = Object.values(PaymentsApiErrorCodes).includes(
    maybe.errorCode as PaymentsApiErrorCodes
  );
  const isValidErrorData = Array.isArray(maybe.errorData);
  const isValidErrorMessage =
    maybe.errorMessage === undefined ||
    maybe.errorMessage === null ||
    typeof maybe.errorMessage === 'string';

  return isValidErrorCode && isValidErrorData && isValidErrorMessage;
}

export const getPaymentsApiError = (error: unknown): PaymentsApiError => {
  if (error instanceof PaymentsApiError) return error;

  if (PaymentsApiException.isPaymentsApiException(error)) {
    error = error.response;
  }

  if (!isPaymentsApiErrorResponse(error)) {
    return new PaymentsApiError(
      paymentsApiErrorMessages[PaymentsApiErrorCodes.GenericUnknownError],
      PaymentsApiErrorCodes.GenericUnknownError
    );
  }

  return new PaymentsApiError(paymentsApiErrorMessages[error.errorCode], error.errorCode);
};

export class PaymentsApiError extends Error {
  public errorCode: PaymentsApiErrorCodes | null;

  constructor(errorMessage: string, errorCode: PaymentsApiErrorCodes | null) {
    super(errorMessage);
    this.errorCode = errorCode;
  }
}

const paymentsApiErrorMessages: Record<PaymentsApiErrorCodes, string> = {
  [PaymentsApiErrorCodes.GenericUnknownError]:
    'Odottamaton virhe maksussa. Yritä hetken päästä uudelleen.',
  [PaymentsApiErrorCodes.GenericOrderNotFound]:
    'Tilausta ei löytynyt. Otathan yhteyttä asiakaspalveluun.',
  [PaymentsApiErrorCodes.GenericOrderAlreadyPaid]:
    'Tilaus on jo maksettu. Tilausvahvistuksen pitäisi löytyä sähköpostista.',
  [PaymentsApiErrorCodes.GenericNoRestaurantFound]: 'Virhe ravintolan hakemisessa.',
  [PaymentsApiErrorCodes.GenericMerchantNotFound]:
    'Virhe ravintolan kauppiastunnuksessa. Otathan yhteyttä asiakaspalveluun.',
  [PaymentsApiErrorCodes.GenericConnectionError]: 'Yhteysvirhe. Yritä hetken päästä uudelleen.',
  [PaymentsApiErrorCodes.GenericSumMismatch]: 'Tilauksen summa ei täsmää.',
  [PaymentsApiErrorCodes.GenericSplitPaymentError]: 'Virhe usean maksutavan tilauksessa.',
  [PaymentsApiErrorCodes.GenericMerchantNotActive]:
    'Virhe maksun käsittelyssä. Ravintolan kauppiastunnus ei ole aktiivinen.',
  [PaymentsApiErrorCodes.GenericInvalidOrderKey]:
    'Virhe maksun käsittelyssä. Tilausavain on virheellinen.',
  [PaymentsApiErrorCodes.AdyenAmountIsMissing]: 'Tilauksen summa puuttuu tai se on virheellinen.',
  [PaymentsApiErrorCodes.AdyenInvalidCardNumber]: 'Virheellinen maksukortin numero.',
  [PaymentsApiErrorCodes.AdyenInvalidCardVariant]:
    'Valitettavasti tämä maksukortti ei käy verkkokaupassa.',
  [PaymentsApiErrorCodes.AdyenCVCInvalidLength]: 'Syöttämäsi CVC-tarkistusnumero on liian lyhyt.',
  [PaymentsApiErrorCodes.AdyenInvalidBillingAddress]: 'Ongelma syöttämäsi osoitteen kanssa.',
  [PaymentsApiErrorCodes.AdyenRecurringNotEnabled]:
    'Toistuvia maksuja ei ole kytketty päälle valitsemassasi ravintolassa.',
  [PaymentsApiErrorCodes.AdyenInvalidBankAccountNumber]: 'Ongelma tilinumeron kanssa.',
  [PaymentsApiErrorCodes.AdyenBankDetailsMissing]: 'Ongelma tai puute maksutiedoissa.',
  [PaymentsApiErrorCodes.AdyenInvalidBankCountryCode]:
    'Valitettavasti tämä maksukortti ei käy verkkokaupassa.',
  [PaymentsApiErrorCodes.AdyenBankCountryNotSupported]:
    'Valitettavasti käyttämäsi pankki ei käy verkkokaupassa.',
  [PaymentsApiErrorCodes.AdyenInvalidDateOfBirth]: 'Syntymäpäivä on väärässä muodossa.',
  [PaymentsApiErrorCodes.AdyenInvalidDeliveryAddress]:
    'Syöttämäsi osoite näyttää olevan väärässä muodossa.',
  [PaymentsApiErrorCodes.AdyenInvalidShopperName]: 'Nimi on syötetty väärässä muodossa.',
  [PaymentsApiErrorCodes.AdyenShopperEmailNotProvided]: 'Pakollinen sähköpostiosoite puuttuu.',
  [PaymentsApiErrorCodes.AdyenTelephoneNumberNotProvided]: 'Pakollinen puhelinnumero puuttuu.',
  [PaymentsApiErrorCodes.AdyenInvalidPhoneNumber]: 'Syöttämäsi puhelinnumero on virheellinen.',
  [PaymentsApiErrorCodes.AdyenAccountHolderNameNotProvided]:
    'Tilin omistajan tiedot ovat puutteelliset.',
  [PaymentsApiErrorCodes.AdyenCardHolderNameNotProvided]:
    'Maksukortin omistajan tiedot ovat puutteelliset.',
  [PaymentsApiErrorCodes.AdyenExpiryDateInvalid]: 'Virhe syöttämäsi maksukortin tiedoissa.',
  [PaymentsApiErrorCodes.AdyenAmountIsNotValid]: 'Virhe tilauksen summan tarkistuksessa.',
  [PaymentsApiErrorCodes.AdyenCurrencyIsNotValid]: 'Tilauksella on väärä valutta.',
  [PaymentsApiErrorCodes.AdyenCVCInvalid]: 'Virhe syöttämäsi maksukortin tiedoissa.',
  [PaymentsApiErrorCodes.AdyenAmountNotAllowedForOperation]:
    'Virhe tilauksen summan tarkistuksessa.',
  [PaymentsApiErrorCodes.AdyenInvalidShopperEmail]: 'Syöttämäsi sähköpostiosoite on virheellinen.',
  [PaymentsApiErrorCodes.AdyenInvalidMerchantAccount]:
    'Virhe ravintolan kauppiastunnuksessa. Ota yhteyttä asiakaspalveluun.',
  [PaymentsApiErrorCodes.AdyenTransactionExceedsAllowedLimit]: 'Summa ylittää sallitun rajan.',
  [PaymentsApiErrorCodes.AdyenApplePayTokenAmountMismatch]:
    'Virhe Apple Pay -maksun tarkistuksessa.',
  [PaymentsApiErrorCodes.AdyenGooglePayTokenExpired]:
    'Google Pay -maksu vanhentui. Yritä hetken päästä uudestaan.',
  [PaymentsApiErrorCodes.AdyenRefusalRefused]: 'Maksua ei hyväksytty. Kokeile toista maksutapaa.',
  [PaymentsApiErrorCodes.AdyenRefusalCardBlocked]:
    'Tilausta ei hyväksytty. Kokeile toista maksutapaa.',
  [PaymentsApiErrorCodes.AdyenRefusalCardExpired]:
    'Syöttämäsi maksukortti on vanhentunut. Kokeile toista maksutapaa.',
  [PaymentsApiErrorCodes.AdyenRefusalInvalidAmount]: 'Virhe tilauksen summan tarkistuksessa.',
  [PaymentsApiErrorCodes.AdyenRefusalInvalidCardNumber]: 'Virhe syöttämäsi maksukortin tiedoissa.',
  [PaymentsApiErrorCodes.AdyenRefusalIssuerUnavailable]:
    'Yhteysongelma pankin kanssa. Yritä hetken päästä uudestaan.',
  [PaymentsApiErrorCodes.AdyenRefusalNotSupported]: 'Maksutapa ei ole tuettu.',
  [PaymentsApiErrorCodes.AdyenRefusal3DNotAuthenticated]: 'Virhe maksun varmentamisessa.',
  [PaymentsApiErrorCodes.AdyenRefusalNotEnoughBalance]: 'Maksu epäonnistui. Tilin saldo ei riitä.',
  [PaymentsApiErrorCodes.AdyenRefusalCancelled]: 'Tilaus peruutettu.',
  [PaymentsApiErrorCodes.AdyenRefusalCvcInvalid]: 'Virhe syöttämäsi maksukortin tiedoissa.',
  [PaymentsApiErrorCodes.AdyenRefusalRestrictedCard]: 'Virhe syöttämäsi maksukortin tiedoissa.',
  [PaymentsApiErrorCodes.AdyenRefusalWithdrawalAmountExceeded]:
    'Maksu epäonnistui. Tilin rajoitukset ylittyivät.',
  [PaymentsApiErrorCodes.AdyenRefusalWithdrawalCountExceeded]:
    'Maksu epäonnistui. Tilin rajoitukset ylittyivät.',
  [PaymentsApiErrorCodes.AdyenRefusalAuthenticationRequired]: 'Virhe käyttäjän varmentamisessa.',
  [PaymentsApiErrorCodes.AdyenRefusal3DSError]: 'Virhe maksun varmentamisessa.',
  [PaymentsApiErrorCodes.AdyenRefusalBlocked]: 'Virhe maksun varmentamisessa.',
  [PaymentsApiErrorCodes.EdenredUnauthorizedError]: 'Virhe Edenred-käyttäjän varmentamisessa.',
  [PaymentsApiErrorCodes.EdenredCardBlockedError]: 'Virhe maksukortin varmentamisessa.',
  [PaymentsApiErrorCodes.EdenredCardInvalidNumber]: 'Virheellinen maksukortin numero.',
  [PaymentsApiErrorCodes.EdenredCardNoMoneyError]: 'Tilin saldo ei riitä.',
  [PaymentsApiErrorCodes.EdenredCardExpiredError]: 'Kortti on vanhentunut.',
  [PaymentsApiErrorCodes.EdenredCardSecurityViolationError]: 'Virhe maksukortin varmentamisessa.',
  [PaymentsApiErrorCodes.EdenredCardDeclinedError]: 'Virhe maksukortin varmentamisessa.',
  [PaymentsApiErrorCodes.EdenredCardTransactionBlockedError]: 'Maksu hylätty.',
  [PaymentsApiErrorCodes.EdenredCardDoNotHonorError]: 'Virhe maksukortin varmentamisessa.',
  [PaymentsApiErrorCodes.EdenredCardNotActivatedError]:
    'Korttia ei ole aktivoitu. Aktivoi kortti ja yritä uudestaan.',
  [PaymentsApiErrorCodes.EdenredComplementaryPaymentMethodNotActiveError]:
    'Edenred-tililtäsi puuttuu maksutapa lounasmaksun ylittäville maksuille.',
  [PaymentsApiErrorCodes.EdenredGatewayBlockedError]: 'Yhteysongelma Edenredin kanssa.',
  [PaymentsApiErrorCodes.EdenredGatewayTimeoutError]: 'Yhteysongelma Edenredin kanssa.',
  [PaymentsApiErrorCodes.EdenredInternalError]: 'Yhteysongelma Edenredin kanssa.',
  [PaymentsApiErrorCodes.KlarnaNoAlcoholAllowed]:
    'Klarna-tilaus ei saa sisältää alkoholituotteita.',
  [PaymentsApiErrorCodes.SplitPaymentTooManyMethods]:
    'Jaettu maksu sisältää liian monta maksutapaa.',
  [PaymentsApiErrorCodes.SplitPaymentTooFewMethods]:
    'Jaetussa maksussa ei ole riittävästi maksutapoja.',
  [PaymentsApiErrorCodes.SplitPaymentSumMismatch]:
    'Jaetun maksun maksutapojen summa ei täsmää ostokseen.',
  [PaymentsApiErrorCodes.SplitPaymentIncorrectNonLastMethod]:
    'Vain viimeinen maksutapa voi olla muu kuin lahjakortti.',
  [PaymentsApiErrorCodes.SplitPaymentMethodNotSupported]:
    'Maksutapa ei ole sallittu osana jaettua maksua.',
  // The following errors are not in use in kotipizza.fi
  [PaymentsApiErrorCodes.LegalEntityNotFound]: '',
  [PaymentsApiErrorCodes.GenericStoreNotFoundInDb]: '',
  [PaymentsApiErrorCodes.GenericMerchantCreationFailed]: '',
  [PaymentsApiErrorCodes.GenericMerchantUpdateFailed]: '',
  [PaymentsApiErrorCodes.GenericValidationError]: '',
  [PaymentsApiErrorCodes.GenericPaymentNotFound]:
    'Virhe maksun hakemisessa. Olethan yhteydessä asiakaspalveluun.',
  [PaymentsApiErrorCodes.AdyenMerchantCreateOrFetchFailed]: '',
  [PaymentsApiErrorCodes.AdyenBusinessLineIdNotFound]: '',
  [PaymentsApiErrorCodes.AdyenUserCreateOrFetchFailed]: '',
  [PaymentsApiErrorCodes.PatchPosPaymentMethodsWithNewStoreFailed]: '',
  [PaymentsApiErrorCodes.AdyenFailedToLoadUsers]: '',
  [PaymentsApiErrorCodes.AdyenFailedToActivateMerchant]: '',
  [PaymentsApiErrorCodes.AdyenRestaurantNoPosMerchantAssigned]: '',
  [PaymentsApiErrorCodes.AdyenCreateStoreFailed]: '',
  [PaymentsApiErrorCodes.AdyenSaveStoreFailed]: '',
  [PaymentsApiErrorCodes.AdyenOnboardingServiceError]: '',
  [PaymentsApiErrorCodes.AdyenOnboardingGenericError]: '',
  [PaymentsApiErrorCodes.AdyenFailedToGetPaymentMethodSettings]: '',
  [PaymentsApiErrorCodes.AdyenFailedToRequestPaymentMethods]: '',
  [PaymentsApiErrorCodes.AdyenFailedToPatchPaymentMethodsWithNewStore]: '',
  [PaymentsApiErrorCodes.AdyenFailedToPatchOrRequestPosPaymentMethod]: '',
  [PaymentsApiErrorCodes.AdyenWebhookError]: '',
  [PaymentsApiErrorCodes.AdyenWebhookInvalidHmac]: '',
  [PaymentsApiErrorCodes.AdyenMerchantUpdateError]: '',
  [PaymentsApiErrorCodes.AdyenMerchantCreateError]: '',
  [PaymentsApiErrorCodes.KlarnaMerchantCreateFailed]: '',
  [PaymentsApiErrorCodes.KlarnaCreateMerchantApiKeyFailed]: '',
  [PaymentsApiErrorCodes.KlarnaMerchantSaveFailed]: '',
  [PaymentsApiErrorCodes.KlarnaMerchantCreateOnboardingWidgetUrlFailed]: '',
  [PaymentsApiErrorCodes.KlarnaMerchantSendOnboardingEmailFailed]: '',
  [PaymentsApiErrorCodes.MarkGiftCardOrderAsPaidError]:
    'Virhe maksun merkitsemisessä. Olethan yhteydessä asiakaspalveluun.',
  [PaymentsApiErrorCodes.MerchantLegalEntityMismatch]: '',
  [PaymentsApiErrorCodes.GetRestaurantLegalEntitiesError]: '',
  [PaymentsApiErrorCodes.MarkGiftCardOrderAsPaidAlreadyInProcessingError]: '',
  [PaymentsApiErrorCodes.EPassiUnauthorizedError]: 'Virhe Epassi-käyttäjän varmentamisessa.',
  [PaymentsApiErrorCodes.EPassiNoWalletToken]:
    'Epassi-lompakkoa ei ole aktivoitu. Aktivoi Epassi-lompakko ja yritä uudelleen.',
  [PaymentsApiErrorCodes.EPassiPaymentError]:
    'Virhe maksun käsittelyssä. Yritä hetken kuluttua uudestaan.',
  [PaymentsApiErrorCodes.EPassiNoFunds]: 'Epassin saldo ei riitä.',
  [PaymentsApiErrorCodes.EPassiExpiredToken]:
    'Epassi-lompakon valtuutus on vanhentunut. Aktivoi Epassi-lompakko uudelleen.',
  [PaymentsApiErrorCodes.EPassiTokenDeactivated]:
    'Epassi-lompakon valtuutus on poistettu käytöstä. Aktivoi Epassi-lompakko uudelleen.',
  [PaymentsApiErrorCodes.EPassiNoSuchToken]:
    'Epassi-lompakkoa ei löytynyt. Aktivoi Epassi-lompakko uudelleen.',
  [PaymentsApiErrorCodes.EPassiCustomerAccountNotActive]: 'Epassi-tilisi ei ole aktiivinen.',
  [PaymentsApiErrorCodes.EPassiNoFundingSourceSupportsSite]:
    'Epassi-tililtäsi puuttuu maksutapa lounasmaksun ylittäville maksuille.',
  [PaymentsApiErrorCodes.EPassiDuplicatedTransaction]: 'Maksu on jo käsitelty aiemmin.',
  [PaymentsApiErrorCodes.EPassiCountryNotSupported]: 'Epassi ei ole käytössä tässä maassa.',
  [PaymentsApiErrorCodes.EPassiSignatureVerificationFailed]: 'Virhe maksun varmentamisessa.',
  [PaymentsApiErrorCodes.EPassiSiteNotActive]:
    'Epassi ei ole käytössä valitsemassasi ravintolassa.',
  [PaymentsApiErrorCodes.EPassiApiKeyInvalid]:
    'Virhe Epassi-yhteydessä. Otathan yhteyttä asiakaspalveluun.',
  [PaymentsApiErrorCodes.EPassiTransactionFailed]:
    'Virhe maksun käsittelyssä. Yritä hetken kuluttua uudestaan.',
};
